import React, { useState } from "react";
import * as dmsService from "../../../services/dmsService";
import * as finService from "../../../services/finService";
import MasterSection from "../../Platform/setup/MasterSection";
import useFinOptions, { optionByCode } from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { act, hint, linkBtn } from "../../Fin/gl/glUi";
import { PrimaryButton, SecondaryButton, SlidePanel, Tabs, inputStyle, useLoad } from "../../../components/ui";

// Dealership Setup tabs added with F&I, after-sales care, fleet and the body shop, and sales targets.
const m = (key) => ({ load: () => dmsService.listMaster(key), save: (id, data) => dmsService.saveMaster(key, id, data) });
const CODE = { key: "code", label: "Code", required: true, max: 24, hint: "Letters, digits, - and _" };
const NAME = { key: "name", label: "Name", required: true, max: 150 };
const opt = (list) => list.map(([value, label]) => ({ label, value }));

function useAccountOptions() {
  const { data } = useLoad(() => finService.listCustomers({ size: 500 }), []);
  return (data || []).map((c) => ({ label: `${c.name} (${c.account_number})`, value: c.account_number }));
}

export function FiSetup() {
  const [section, setSection] = useState("lenders");
  const accounts = useAccountOptions();
  const fin = useFinOptions(["suppliers", "tax-rates"]);
  return (
    <>
      <Tabs tabs={[{ key: "lenders", label: "Lenders" }, { key: "products", label: "F&I products" }]} active={section} onChange={setSection} />
      {section === "lenders" ? (
        <MasterSection
          {...m("fi-lenders")}
          noun="lender"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Type", (r) => r.lender_type], ["Account", (r) => r.customer_account_number], ["Reserve", (r) => `${Number(r.reserve_pct)}% + ${fmtMoney(r.flat_fee)}`]]}
          fieldKeys={["name", "lender_type", "customer_account_number", "reserve_pct", "flat_fee"]}
          lockedOnEdit={["code"]}
          defaults={{ lender_type: "BANK", reserve_pct: 0, flat_fee: 0 }}
          fields={() => [
            CODE,
            NAME,
            { key: "lender_type", label: "Type", type: "select", required: true, options: opt([["BANK", "Bank"], ["FINANCE_CO", "Finance company"], ["LEASING", "Leasing company"]]) },
            { key: "customer_account_number", label: "The lender in Receivables (it pays the amount financed)", type: "select", required: true, options: accounts },
            { key: "reserve_pct", label: "Dealer reserve % of the amount financed", type: "number", step: "any", min: 0 },
            { key: "flat_fee", label: "Flat fee per deal", type: "number", step: "any", min: 0 },
          ]}
        />
      ) : (
        <MasterSection
          {...m("fi-products")}
          noun="product"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Type", (r) => r.product_type], ["Cost", (r) => fmtMoney(r.cost)], ["Price", (r) => fmtMoney(r.price)], ["Provider", (r) => r.provider_supplier_code]]}
          fieldKeys={["name", "product_type", "provider_supplier_code", "cost", "price", "tax_rate_code", "term_months"]}
          lockedOnEdit={["code"]}
          fields={() => [
            CODE,
            NAME,
            { key: "product_type", label: "Type", type: "select", required: true, options: opt([["EXT_WARRANTY", "Extended warranty"], ["GAP", "GAP cover"], ["CREDIT_LIFE", "Credit life"], ["SERVICE_PLAN", "Service plan"], ["OTHER", "Other"]]) },
            { key: "provider_supplier_code", label: "Provider (supplier)", type: "select", required: true, options: (fin.suppliers || []).map((s) => ({ label: s.label, value: s.code })) },
            { key: "cost", label: "Cost (owed to the provider)", type: "number", step: "any", min: 0, required: true },
            { key: "price", label: "List price", type: "number", step: "any", min: 0, required: true },
            { key: "tax_rate_code", label: "VAT code", type: "select", options: (fin["tax-rates"] || []).map((t) => ({ label: t.label, value: t.code })) },
            { key: "term_months", label: "Term (months)", type: "number", min: 0 },
          ]}
        />
      )}
    </>
  );
}

export function FleetInsurersSetup() {
  const [section, setSection] = useState("fleet");
  const accounts = useAccountOptions();
  return (
    <>
      <Tabs tabs={[{ key: "fleet", label: "Fleet customers" }, { key: "insurers", label: "Insurers" }]} active={section} onChange={setSection} />
      {section === "fleet" ? (
        <MasterSection
          {...m("fleet-accounts")}
          noun="fleet customer"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Account", (r) => r.customer_account_number], ["Contact", (r) => [r.contact_name, r.contact_phone].filter(Boolean).join(" ")]]}
          fieldKeys={["name", "customer_account_number", "contact_name", "contact_phone"]}
          lockedOnEdit={["code"]}
          fields={() => [CODE, NAME, { key: "customer_account_number", label: "Receivables account", type: "select", required: true, options: accounts }, { key: "contact_name", label: "Contact", max: 150 }, { key: "contact_phone", label: "Phone", max: 30 }]}
        />
      ) : (
        <MasterSection
          {...m("insurers")}
          noun="insurer"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Account", (r) => r.customer_account_number]]}
          fieldKeys={["name", "customer_account_number"]}
          lockedOnEdit={["code"]}
          fields={() => [CODE, NAME, { key: "customer_account_number", label: "Receivables account (it pays the approved repairs)", type: "select", required: true, options: accounts }]}
        />
      )}
    </>
  );
}

function SurveyQuestions({ survey, onHide }) {
  const { data, loading } = useLoad(() => dmsService.listSurveyQuestions(survey.id), [survey.id]);
  const [rows, setRows] = useState(null);
  const list = rows || (data || []).map((q) => ({ question: q.question, weight: Number(q.weight) }));
  const set = (i, patch) => setRows(list.map((q, k) => (k === i ? { ...q, ...patch } : q)));
  const save = async () => (await act(() => dmsService.saveSurveyQuestions(survey.id, list))) && onHide();
  return (
    <SlidePanel show onHide={onHide} title={`Questions – ${survey.name}`} width={680} footer={<><SecondaryButton onClick={onHide}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={loading}>Save</PrimaryButton></>}>
      {list.map((q, i) => (
        <div key={i} className="d-flex gap-2 mb-2">
          <input className="form-control" style={{ ...inputStyle, flex: 1 }} aria-label={`Question ${i + 1}`} value={q.question} onChange={(e) => set(i, { question: e.target.value })} />
          <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 90 }} aria-label="Weight" title="Weight" value={q.weight} onChange={(e) => set(i, { weight: e.target.value })} />
          <button type="button" style={linkBtn} onClick={() => setRows(list.filter((_, k) => k !== i))}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRows([...list, { question: "", weight: 1 }])}>
        Add question
      </button>
      <div style={{ ...hint, marginTop: 8 }}>Answers are scores on the survey's scale; the weights make some questions count more.</div>
    </SlidePanel>
  );
}

export function AftercareSetup() {
  const [section, setSection] = useState("schedules");
  const opts = useDmsOptions(["models", "makes", "labour-operations"]);
  const [questionsFor, setQuestionsFor] = useState(null);
  return (
    <>
      <Tabs
        tabs={[
          { key: "schedules", label: "Service schedules" },
          { key: "retention", label: "Win-back campaigns" },
          { key: "csi", label: "CSI surveys" },
        ]}
        active={section}
        onChange={setSection}
      />
      {section === "schedules" ? (
        <MasterSection
          {...m("service-schedules")}
          noun="service schedule"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Model", (r) => r.model_name || "Every model"], ["Every", (r) => [r.interval_km ? `${r.interval_km} km` : null, r.interval_months ? `${r.interval_months} months` : null].filter(Boolean).join(" or ")]]}
          fieldKeys={["name", "model_id", "interval_km", "interval_months", "labour_op_id"]}
          lockedOnEdit={["code"]}
          toForm={(r) => ({ ...r, model_id: optionByCode(opts.models, r.model_code), labour_op_id: optionByCode(opts["labour-operations"], r.labour_op_code) })}
          fields={() => [
            CODE,
            { ...NAME, max: 100 },
            { key: "model_id", label: "Model (blank: every model)", type: "select", options: opts.models || [] },
            { key: "interval_km", label: "Every (km)", type: "number", min: 0 },
            { key: "interval_months", label: "Every (months)", type: "number", min: 0 },
            { key: "labour_op_id", label: "Labour operation", type: "select", options: opts["labour-operations"] || [] },
          ]}
        />
      ) : null}
      {section === "retention" ? (
        <MasterSection
          {...m("retention-campaigns")}
          noun="campaign"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Not seen for", (r) => `${r.inactive_days} days`], ["Make", (r) => r.make_name || "Any"]]}
          fieldKeys={["name", "inactive_days", "make_id", "message"]}
          lockedOnEdit={["code"]}
          defaults={{ inactive_days: 365 }}
          toForm={(r) => ({ ...r, make_id: optionByCode(opts.makes, r.make_code) })}
          extraActions={(r) => (
            <button type="button" style={linkBtn} onClick={() => act(() => dmsService.runRetention(r.id))}>
              Add to call list
            </button>
          )}
          fields={() => [CODE, { ...NAME, max: 100 }, { key: "inactive_days", label: "Customers not seen for (days)", type: "number", min: 30, required: true }, { key: "make_id", label: "Make (blank: any)", type: "select", options: opts.makes || [] }, { key: "message", label: "What to tell them", type: "textarea", max: 500 }]}
        />
      ) : null}
      {section === "csi" ? (
        <MasterSection
          {...m("csi-surveys")}
          noun="survey"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Sent after", (r) => (r.trigger_event === "RO" ? "Service visit" : "Delivery")], ["Scale", (r) => `1–${r.scale_max}`], ["Follow up below", (r) => Number(r.alert_below)]]}
          fieldKeys={["name", "trigger_event", "scale_max", "alert_below"]}
          lockedOnEdit={["code"]}
          defaults={{ trigger_event: "RO", scale_max: 5, alert_below: 3 }}
          extraActions={(r) => (
            <button type="button" style={linkBtn} onClick={() => setQuestionsFor(r)}>
              Questions
            </button>
          )}
          fields={() => [CODE, { ...NAME, max: 100 }, { key: "trigger_event", label: "Sent after", type: "select", required: true, options: opt([["DELIVERY", "A vehicle delivery"], ["RO", "A service visit (gate pass)"]]) }, { key: "scale_max", label: "Scale (1 to)", type: "number", min: 3, required: true }, { key: "alert_below", label: "Follow up when the score is below", type: "number", step: "any", min: 0, required: true }]}
        />
      ) : null}
      {questionsFor ? <SurveyQuestions survey={questionsFor} onHide={() => setQuestionsFor(null)} /> : null}
    </>
  );
}

export function TargetsSetup() {
  const fin = useFinOptions(["locations"]);
  return (
    <MasterSection
      {...m("targets")}
      noun="target"
      columns={[["Month", (r) => r.period_month], ["Consultant", (r) => r.salesperson_employee_name || ""], ["Vehicles", (r) => r.units_target], ["Revenue", (r) => fmtMoney(r.revenue_target)]]}
      fieldKeys={["location_id", "units_target", "revenue_target"]}
      lockedOnEdit={["period_month", "salesperson_employee_id"]}
      defaults={{ period_month: new Date().toISOString().slice(0, 7) }}
      toForm={(r) => ({ ...r, location_id: optionByCode(fin.locations, r.location_code) })}
      fields={() => [
        { key: "period_month", label: "Month (YYYY-MM)", required: true, max: 7 },
        { key: "salesperson_employee_id", label: "Sales consultant", type: "employee", required: true },
        { key: "location_id", label: "Location", type: "select", options: fin.locations || [] },
        { key: "units_target", label: "Vehicles", type: "number", min: 0, required: true },
        { key: "revenue_target", label: "Revenue (ledger currency)", type: "number", step: "any", min: 0 },
      ]}
    />
  );
}
